import {useState, useMemo, useDeferredValue, useCallback} from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import {AddCircleRounded} from '@mui/icons-material'
import ClientAccordion from './ClientAccordion'
import {useGetClientDetails} from '../api/getClientDetails'
import UserMessages from '../common/Errors/UserMessages.json'
import {CenteredErrorContainer} from '../ProjectDetails/ProjectDetailStyle'
import DataRefetchOnError from '../common/DataFetchError/DataRefetchOnError'
import EmptyListAlertInfo from '../common/EmptyListAlertInfo/EmptyListAlertInfoIndex'
import AddClientModal from './AddClientModal'
import {StyledSearchField, StyledSearchIcon} from './ManageClientPermissionsStyle'
import {StyledTitle} from '../AdminPanelStyling'
import {useRolePermissions} from '../../hooks/useRolesPermissions'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import SnackbarMessages from '../common/Snackbar/SnackbarMessages.json'

const ManageClientPermissions = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddClientModal, setShowAddClientModal] = useState(false)
  const [successSnackbar, setSuccessSnackbar] = useState({show: false, message: ''})

  const {isSuperAdmin, isCustomerSupport} = useRolePermissions()

  const {clientData, loadingClients, error, refetch} = useGetClientDetails()
  const deferredSearchQuery = useDeferredValue(searchQuery)

  const filteredClients = useMemo(() => {
    const clients = clientData?.clientList ?? []
    const normalizedSearchQuery = deferredSearchQuery.trim().toLowerCase()
    if (!normalizedSearchQuery) return clients
    return clients.filter((client) =>
      String(client?.CLIENT_NAME ?? '')
        .toLowerCase()
        .includes(normalizedSearchQuery)
    )
  }, [clientData?.clientList, deferredSearchQuery])

  const refetchClients = useCallback(
    (event) => {
      event?.preventDefault()
      return refetch()
    },
    [refetch]
  )

  const handleClientCreated = useCallback(async () => {
    await refetch()
    setSuccessSnackbar({show: true, message: SnackbarMessages.clientAdded})
  }, [refetch])

  const handleClientUpdated = useCallback(async () => {
    await refetch()
    setSuccessSnackbar({show: true, message: SnackbarMessages.clientUpdateSuccess})
  }, [refetch])

  const renderedClientRows = useMemo(
    () =>
      filteredClients.map((client, index) => (
        <ClientAccordion
          key={`${String(client?.CLIENT_ID ?? '')}-${String(client?.CLIENT_NAME ?? '')}-${index}`}
          client={client}
          refetchClients={refetchClients}
          onClientUpdated={handleClientUpdated}
        />
      )),
    [filteredClients, handleClientUpdated, refetchClients]
  )

  const handleAddClient = (event) => {
    event.preventDefault()
    setShowAddClientModal(true)
  }

  const closeAddClientModal = () => {
    setShowAddClientModal(false)
  }

  if (loadingClients)
    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
        <CircularProgress />
      </Box>
    )

  if (!loadingClients && error) {
    return (
      <CenteredErrorContainer>
        <DataRefetchOnError onTryAgain={(event) => refetchClients(event)} />
      </CenteredErrorContainer>
    )
  }
  if (!loadingClients && clientData?.clientList?.length === 0) {
    return <EmptyListAlertInfo message={UserMessages.clientNotFound} />
  }

  return (
    <>
      {showAddClientModal && (
        <AddClientModal
          open={showAddClientModal}
          onClose={closeAddClientModal}
          onSave={handleClientCreated}
          clients={clientData?.clientList ?? []}
        />
      )}
      <Box display='flex' flexDirection='column' gap={2} margin={0}>
        <StyledTitle>Clients & Permissions</StyledTitle>
        <Box display='flex' gap={2} alignItems='center'>
          <StyledSearchField
            size='medium'
            placeholder='Search Clients'
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <StyledSearchIcon />
                </InputAdornment>
              )
            }}
          />

          {(isSuperAdmin() || isCustomerSupport()) && (
            <Button variant='outlined' size='medium' startIcon={<AddCircleRounded />} onClick={handleAddClient}>
              Add New Client
            </Button>
          )}
        </Box>
        {filteredClients.length === 0 ? (
          <EmptyListAlertInfo message={UserMessages.clientNotFound} />
        ) : (
          renderedClientRows
        )}
      </Box>
      <SnackbarComponent
        showNotification={successSnackbar.show}
        setShowNotification={(show) => setSuccessSnackbar((prev) => ({...prev, show}))}
        message={successSnackbar.message}
        error={false}
      />
    </>
  )
}

export default ManageClientPermissions
